import { DatePipe, DecimalPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewEncapsulation,
  computed,
  inject,
  input,
  model,
  output,
  signal,
  viewChild
} from '@angular/core';
import { outputFromObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ENVIRONMENT, TranslateLanguageService, UserService } from '@cfp/core';
import { AiBuildingRoadmapButton } from '@cfp/features-shared/ai';
import { BuildingMeasureTableItemDTOWithYearData } from '@cfp/features-shared/interfaces/year-data';
import { OwnershipStatusToggle } from '@cfp/features-shared/ownership-status';
import { BuildingAddress } from '@cfp/shared/building/address-module';
import { MANUAL_ENERGY_LABELS } from '@cfp/shared/energy';
import { EnergyLabel } from '@cfp/shared/energy-label';
import { RoadmapService } from '@cfp/shared/roadmap';
import { LabelGroupModule } from '@cfp/shared/ui/label-group';
import { LineClampDetectorDirective } from '@cfp/shared/ui/line-clamp-detector';
import {
  MeasurementArea,
  MeasurementCostPerSquare,
  MeasurementKwhPerSquare,
  MeasurementMassPerSquare,
  MeasurementService
} from '@cfp/shared/ui/measurement';
import {
  BreakpointObserver,
  Button,
  ButtonToggle,
  Card,
  CfpCurrencyPipe,
  Co2Module,
  CurrencyModule,
  Dropdown,
  DropdownOption,
  DropdownSelect,
  ExcelExportModule,
  Icon,
  InputCheckbox,
  InputRadio,
  LiveRegionStatusUpdaterModule,
  LoadingSpinner,
  OrderByPipe,
  SortIcon,
  TableStickyHeaderDirective,
  TextWrapWithContent
} from '@cfp/ui';
import { Co2Pipe } from '@cfp/ui/pipes';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  BuildingMeasureTableItemDTO,
  BuildingRoadmapAIEstimationDTO,
  CurrencyCodeDTO,
  LabelGroupDTO,
  MeasureStatusDTO,
  RegisteredOrCalculatedEnergyLabelDTO,
  RoadmapBuildingYearDTO,
  RoadmapPortfolioBuildingDetailsDTO
} from 'csr-asset-app';
import { LanguageCodeDTO } from 'csr-login-app';
import { TooltipDirective } from 'ngx-bootstrap/tooltip';
import { Subject, Subscription, debounceTime, map } from 'rxjs';
import { RoadmapPortfolioBuildingDetailsDTOWithYearData, Year } from '../../interfaces';
import {
  RoadmapTableExcelExportService,
  RoadmapTableImageExportService,
  RoadmapTableShowProperty
} from '../../services';
import { MeasureStatusActionDropdown } from './measure-status-action-dropdown';

const ROADMAP_TABLE_SHOW_PROPERTY_NAME = 'roadmap-table-show-property';

@Component({
  selector: 'cfp-roadmap-table',
  templateUrl: './roadmap-table.html',
  styleUrls: ['./roadmap-table.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    Co2Module,
    CurrencyModule,
    EnergyLabel,
    TranslateModule,
    TextWrapWithContent,
    BuildingAddress,
    LiveRegionStatusUpdaterModule,
    SortIcon,
    InputRadio,
    FormsModule,
    LoadingSpinner,
    Dropdown,
    Card,
    NgTemplateOutlet,
    ReactiveFormsModule,
    DropdownOption,
    MeasureStatusActionDropdown,
    TooltipDirective,
    AiBuildingRoadmapButton,
    TableStickyHeaderDirective,
    DropdownSelect,
    DecimalPipe,
    ButtonToggle,
    MeasurementArea,
    MeasurementMassPerSquare,
    MeasurementCostPerSquare,
    MeasurementKwhPerSquare,
    OwnershipStatusToggle,
    Button,
    LineClampDetectorDirective,
    LabelGroupModule,
    ExcelExportModule,
    Icon,
    InputCheckbox
  ],
  providers: [RoadmapTableExcelExportService, RoadmapTableImageExportService, DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RoadmapTable implements OnInit, OnChanges, OnDestroy {
  private readonly currentYear: number = new Date().getFullYear();

  public readonly labelGroup = input.required<LabelGroupDTO | undefined>();
  public readonly years = input.required<Partial<RoadmapBuildingYearDTO>[] | Year[]>();
  public readonly rows =
    model.required<(RoadmapPortfolioBuildingDetailsDTOWithYearData | BuildingMeasureTableItemDTOWithYearData)[]>();
  public readonly loading = input<boolean>(false);
  public readonly displayStartYear = input<number>(this.currentYear);
  public readonly predicting = input<boolean>(false);
  public readonly predictingChange = output<boolean>();
  public readonly showAiPredictingButton = input<boolean>(false);
  public readonly hasAIPredictedData = input<boolean>(false);
  public readonly saving = input<boolean>(false);
  public readonly readOnly = input<boolean>(false);
  public readonly errorMessage = input<string | undefined>(undefined);
  public readonly totals = input<any>();
  public readonly showCellButton = input<boolean>(false);
  public readonly leftColumnName = input<'name' | 'displayAddress'>('name');
  public readonly leftColumnLabelKey = input<'code' | 'id'>('code');
  public readonly defaultSortColumnName = input<'co2ConsumptionReduction' | 'orderByAddress'>(
    'co2ConsumptionReduction'
  );
  public readonly defaultSortDescending = input<boolean>(false);
  public readonly sortYearNull = input<boolean>(false);
  public readonly showAddRow = input<boolean>(false);
  public readonly showEnergyLabel = input<boolean>(false);
  public readonly showNoRowsContent = input<boolean>(false);
  public readonly showImplementedMeasuresToggle = input<boolean>(true);
  public readonly currencyCode = input<CurrencyCodeDTO | undefined>(undefined);
  public readonly useH1 = input<boolean>(false);
  public readonly hasMeasureStatusesFilter = input<boolean | undefined>(undefined);
  public readonly showM2 = model<boolean>(false);
  public readonly showM2Toggle = input<boolean>(false);
  public readonly showEnergyLabelInRow = input<boolean>(false);
  public readonly buildingId = input<number | undefined>(undefined);
  public readonly resetSort = input<boolean>(false);
  public readonly canChangeUserDefinedEnergyLabel = input<boolean>(false);
  public readonly canResetUserDefinedEnergyLabel = input<boolean>(false);
  public readonly inChangeEnergyLabelMode = model<boolean>(false);
  public readonly showLabelGroup = input<boolean>(false);

  public readonly rowClick = output<any>();
  public readonly predict = output<BuildingRoadmapAIEstimationDTO>();
  public readonly energyLabelChange = output<{ year: number; label: string | null }>();
  public readonly energyLabelEdit = output<void>();
  public readonly energyLabelSave = output<void>();
  public readonly energyLabelCancel = output<void>();
  public readonly energyLabelReset = output<void>();

  protected readonly isMeasuresRoadmap = computed<boolean>(() => this.leftColumnName() === 'name');

  protected readonly focusIndex = signal<number | undefined>(undefined);
  protected readonly openIndex = signal<number | undefined>(undefined);
  protected restoreFocusElement?: HTMLElement;

  protected cellClickEmitter = new Subject<any>();
  public readonly cellClick = outputFromObservable(this.cellClickEmitter);

  public readonly toggleCompletedMeasures = output<boolean>();
  public readonly measureStatusUpdate = output<{
    buildingMeasure: BuildingMeasureTableItemDTO;
    newStatus: MeasureStatusDTO;
  }>();

  protected readonly tableContainerWidth = signal<number | undefined>(undefined);
  protected readonly headerSticky = signal(false);
  protected readonly headerHidden = signal(false);
  protected readonly showProperty = signal<RoadmapTableShowProperty>('investment');
  protected readonly sortColumnName = signal<string>('co2ConsumptionReduction');
  protected readonly sortYear = signal<number | undefined>(undefined);
  protected readonly sortDescending = signal(false);
  protected readonly canChangeRoadmap = signal(false);
  protected readonly thWidth = signal(112);
  protected readonly thHeight = signal(68);
  protected readonly theadHeight = signal(0);
  protected readonly canUpdateAriaStatus = signal(false);
  protected toggleSwitchCompletedMeasures!: FormControl;
  protected readonly isBuildingRoadmap = computed(() => this.leftColumnName() === 'displayAddress');
  protected readonly retainFocusIndex = signal(false);

  protected readonly table = viewChild<ElementRef<HTMLDivElement>>('table');
  protected readonly tableContainer = viewChild<ElementRef<HTMLDivElement>>('tableContainer');

  protected readonly MeasureStatusDTO = MeasureStatusDTO;
  protected readonly manualEnergyLabels = [null, ...MANUAL_ENERGY_LABELS];

  protected readonly environment = inject(ENVIRONMENT);
  private readonly orderByPipe = inject(OrderByPipe);
  private readonly co2Pipe = inject(Co2Pipe);
  private readonly cfpCurrencyPipe = inject(CfpCurrencyPipe);
  private readonly userService = inject(UserService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly translateService = inject(TranslateService);
  private readonly translateLanguageService = inject(TranslateLanguageService);
  private readonly measurementService = inject(MeasurementService);
  private readonly roadmapService = inject(RoadmapService);
  private readonly roadmapTableExcelExportService = inject(RoadmapTableExcelExportService);
  private readonly roadmapTableImageExportService = inject(RoadmapTableImageExportService);

  protected readonly showOwnershipStatus = this.environment.components.canChangeOwnershipStatus;

  protected readonly hasAILicense = toSignal(this.userService.hasLicense('ai'), { initialValue: false });
  protected readonly showExportToExcel = toSignal(this.userService.hasLicense('export_to_excel'), {
    initialValue: false
  });
  protected readonly showRoadmapImageExport = toSignal(this.userService.hasLicense('roadmap_image_export'), {
    initialValue: false
  });
  protected readonly currentLang = signal<LanguageCodeDTO>(
    this.translateLanguageService.getCurrentSupportedLanguageCode()
  );

  private readonly isBreakpointXsOrSm = toSignal(
    this.breakpointObserver.observe().pipe(map((state) => state.breakpoints.xs || state.breakpoints.sm))
  );

  protected readonly showDropdown = this.isBreakpointXsOrSm;
  protected readonly showDesktopOptionsDropdown = computed(
    () =>
      this.showExportToExcel() ||
      this.showRoadmapImageExport() ||
      (!this.isBuildingRoadmap() && this.showImplementedMeasuresToggle())
  );

  private measureImplementationYearModalOpenChangeSubscription?: Subscription;
  private isMeasureImplementationYearModalOpen: boolean = false;

  public ngOnInit(): void {
    this.toggleSwitchCompletedMeasures = new FormControl<any>(this.hasMeasureStatusesFilter());
    this.toggleSwitchCompletedMeasures.valueChanges
      .pipe(debounceTime(500))
      .pipe(
        map((value) => {
          this.toggleCompletedMeasures.emit(value);
        })
      )
      .subscribe();

    let showProperty = localStorage.getItem(ROADMAP_TABLE_SHOW_PROPERTY_NAME);
    if (showProperty) {
      this.showProperty.set(showProperty as RoadmapTableShowProperty);

      if (this.showProperty() === 'energyLabels' && !this.showEnergyLabel()) {
        this.showProperty.set('investment');
      }
    }

    this.canChangeRoadmap.set(this.userService.hasScope('buildings-roadmap-modify'));
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.rows || changes.defaultSortColumnName || changes.defaultSortDescending) {
      if (this.resetSort()) {
        this.sortDescending.set(this.defaultSortDescending());
        this.sortColumnName.set(this.defaultSortColumnName());
        this.sortYear.set(undefined);
        this.sortRows();
      }

      setTimeout(() => {
        const table = this.table();
        if (table) {
          // The table start year is the start year of the roadmap, this is the year of the first column, often 2020
          const tableStartYear = this.roadmapService.getStartYear();
          // Scroll to the first display start column (default the current year)
          const numberOfColumnsToScroll = this.displayStartYear() - tableStartYear;

          const th = table.nativeElement.querySelector('th.year');
          const thead = table.nativeElement.querySelector('thead');
          const tableContainer = this.tableContainer();
          if (th && thead && tableContainer) {
            this.thWidth.set(th.clientWidth + 1);
            this.thHeight.set(th.clientHeight + 1);
            this.theadHeight.set(thead.clientHeight + 1);
            tableContainer.nativeElement.scrollLeft = numberOfColumnsToScroll * this.thWidth();
            this.tableContainerWidth.set(tableContainer.nativeElement.clientWidth);
          }
        }

        this.canUpdateAriaStatus.set(true);
      });
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribeMeasureImplementationYearModalOpenChangeSubscription();
  }

  protected openMeasureStatusDropdown(index: number, event: MouseEvent | Event): void {
    event.preventDefault();
    event.stopImmediatePropagation();

    this.restoreFocusElement = (event.target as HTMLElement).closest('th')?.querySelector('button') || undefined;
    this.openIndex.set(index);
    this.retainFocusIndex.set(true);
  }

  protected measureStatusDropdownClosed() {
    setTimeout(() => {
      // Timeout is need for timing issue with setting isMeasureImplementationYearModalOpen
      if (!this.isMeasureImplementationYearModalOpen) {
        this.openIndex.set(undefined);
        this.retainFocusIndex.set(false);
      }
    });
  }

  protected squareUnit(): string {
    return this.measurementService.squareUnit();
  }

  protected mapToRoadmapBuildingYearDTO(year: Partial<RoadmapBuildingYearDTO> | Year): RoadmapBuildingYearDTO {
    return year as RoadmapBuildingYearDTO;
  }

  protected mapToBuildingMeasureTableItemDTOWithYearData(
    row: RoadmapPortfolioBuildingDetailsDTO | BuildingMeasureTableItemDTOWithYearData
  ): BuildingMeasureTableItemDTOWithYearData {
    return row as BuildingMeasureTableItemDTOWithYearData;
  }

  protected mapToEnergyLabels(energyLabels: any): { [key: string]: RegisteredOrCalculatedEnergyLabelDTO } {
    return energyLabels as { [key: string]: RegisteredOrCalculatedEnergyLabelDTO };
  }

  protected mapToRoadmapPortfolioBuildingDetailsDTO(
    row: RoadmapPortfolioBuildingDetailsDTO | BuildingMeasureTableItemDTOWithYearData
  ): RoadmapPortfolioBuildingDetailsDTOWithYearData {
    return row as RoadmapPortfolioBuildingDetailsDTOWithYearData;
  }

  protected getRowTrackKey(
    row: RoadmapPortfolioBuildingDetailsDTOWithYearData | BuildingMeasureTableItemDTOWithYearData
  ): string | number {
    if (this.leftColumnName() === 'displayAddress') {
      const building = this.mapToRoadmapPortfolioBuildingDetailsDTO(row);
      return building.id ?? building.orderByAddress ?? building.displayAddress?.join(',') ?? '';
    }

    const measure = this.mapToBuildingMeasureTableItemDTOWithYearData(row);
    return measure.id ?? measure.name ?? '';
  }

  protected shouldShowCellButtonTitle(
    year: Partial<RoadmapBuildingYearDTO> | Year,
    row: BuildingMeasureTableItemDTOWithYearData
  ): boolean {
    return this.showCellButton() && this.canChangeRoadmap() && !!row.cols && !row.cols[year.year || -1];
  }

  protected onRowClick(
    row: RoadmapPortfolioBuildingDetailsDTO | BuildingMeasureTableItemDTOWithYearData,
    index: number
  ): void {
    this.rowClick.emit({ ...row, ...{ index } });
  }

  protected isSortActive(year?: number | null): boolean {
    if (year === undefined && (this.sortColumnName() === 'orderByAddress' || this.sortColumnName() === 'name')) {
      return true;
    }
    return year === this.sortYear() && this.sortColumnName() === this.showProperty();
  }

  protected getAriaSort(year?: number | null): string {
    if (this.isSortActive(year)) {
      return this.sortDescending() ? 'descending' : 'ascending';
    }
    return 'none';
  }

  protected onCellClick(
    event: Event,
    data: {
      year: Partial<RoadmapBuildingYearDTO> | Year;
      row: RoadmapPortfolioBuildingDetailsDTO | BuildingMeasureTableItemDTOWithYearData;
      property: string;
    }
  ): void {
    event.stopImmediatePropagation();
    this.cellClickEmitter.next(data);
  }

  protected setShowProperty(showProperty: RoadmapTableShowProperty): void {
    this.showProperty.set(showProperty);
    localStorage.setItem(ROADMAP_TABLE_SHOW_PROPERTY_NAME, showProperty);
  }

  protected export(): void {
    if (!this.showExportToExcel()) {
      return;
    }

    this.roadmapTableExcelExportService.export(
      this.rows(),
      this.years(),
      this.totals(),
      this.showProperty(),
      this.leftColumnName(),
      this.currencyCode(),
      this.labelGroup(),
      this.showM2(),
      this.showEnergyLabelInRow(),
      this.buildingId()
    );
  }

  protected exportToPng(): void {
    if (!this.showRoadmapImageExport()) {
      return;
    }
    const tableContainer = this.tableContainer()?.nativeElement;
    if (!tableContainer) {
      return;
    }
    void this.roadmapTableImageExportService.exportToPng(tableContainer, this.buildingId());
  }

  protected exportToJpg(): void {
    if (!this.showRoadmapImageExport()) {
      return;
    }
    const tableContainer = this.tableContainer()?.nativeElement;
    if (!tableContainer) {
      return;
    }
    void this.roadmapTableImageExportService.exportToJpg(tableContainer, this.buildingId());
  }

  protected getMeasureIconClass(row: BuildingMeasureTableItemDTOWithYearData): string | null {
    if (row.buildingMeasureType === 'USER_DEFINED') {
      return `${this.environment.icons.iconUserDefinedMeasure} ms-2`;
    } else if (row.status === MeasureStatusDTO.DONE) {
      return 'fa fa-check text-success ms-2';
    }
    return null;
  }

  protected getMeasureTitle(row: BuildingMeasureTableItemDTOWithYearData): string {
    if (row.buildingMeasureType === 'USER_DEFINED') {
      return 'measure.userDefined';
    } else if (row.status === MeasureStatusDTO.DONE) {
      return 'implementedIn';
    }
    return '';
  }

  protected getCellButtonTitle(year?: number | null): string {
    if (!year) {
      return 'roadmap.removeYear';
    } else if (year < this.currentYear) {
      return 'roadmap.done';
    } else {
      return 'roadmap.plan';
    }
  }

  protected getCellButtonAriaLabel(
    row: RoadmapPortfolioBuildingDetailsDTO | BuildingMeasureTableItemDTOWithYearData,
    year?: number | null
  ): string | undefined {
    let ariaLabel: string | undefined;
    if (!year && (row as BuildingMeasureTableItemDTOWithYearData).implementationYear) {
      return `${this.translateService.instant('roadmap.removeYear')} ${(row as BuildingMeasureTableItemDTOWithYearData).implementationYear}`;
    } else if (year && (row as BuildingMeasureTableItemDTOWithYearData).cols?.[year]) {
      ariaLabel = `Edit${year ? ` ${year}` : ''}`;

      switch (this.showProperty()) {
        case 'energyLabels':
          const labelGroup = this.labelGroup();
          if (labelGroup) {
            ariaLabel += `, Energy label ${(row as RoadmapPortfolioBuildingDetailsDTO).energyLabels[labelGroup].currentEnergyLabel.label} to ${(row as RoadmapPortfolioBuildingDetailsDTO).energyLabels[labelGroup].improvedEnergyLabel.label}`;
          }
          break;
        case 'co2ConsumptionReduction':
          ariaLabel += `, CO2 reduction of ${this.co2Pipe.transform((row as BuildingMeasureTableItemDTOWithYearData).co2ConsumptionReduction, false)}`;
          break;
        case 'investment':
          ariaLabel += `, investment of ${this.cfpCurrencyPipe.transform((row as BuildingMeasureTableItemDTOWithYearData).investment, this.currencyCode()!)}`;
          break;
        case 'costReduction':
          ariaLabel += `, cost reduction of ${this.cfpCurrencyPipe.transform((row as BuildingMeasureTableItemDTOWithYearData).costReduction, this.currencyCode()!)}`;
          break;
      }
    } else if (year && year < this.currentYear) {
      return `${this.translateService.instant('roadmap.done')} ${year}`;
    } else {
      return `${this.translateService.instant('roadmap.plan')} ${year}`;
    }

    return ariaLabel;
  }

  protected sort(columnName: string, year?: number): void {
    const sortYear = this.sortYearNull() ? year : year || undefined;

    if (this.sortColumnName() === columnName && this.sortYear() === sortYear) {
      this.sortDescending.update((value) => !value);
    } else {
      this.sortDescending.set(year === undefined ? false : true);
      this.sortColumnName.set(columnName);
      this.sortYear.set(sortYear);
    }

    this.sortRows();
  }

  private sortRows(): void {
    if (!this.rows()) {
      return;
    }

    const sortYear = this.sortYear();
    const sortColumnName = this.sortColumnName();
    if (this.hasAILicense() && this.hasAIPredictedData()) {
      this.rows.update((rows) => {
        // sort by planned years
        return rows.sort((rowA: any, rowB: any) => {
          return Object.keys(rowA.cols)[0] < Object.keys(rowB.cols)[0] ? -1 : 1;
        });
      });
    } else if (sortYear === undefined) {
      this.rows.update((rows) =>
        this.orderByPipe.transform<
          RoadmapPortfolioBuildingDetailsDTOWithYearData | BuildingMeasureTableItemDTOWithYearData
        >(rows, sortColumnName, this.sortDescending())
      );
    } else if (sortYear && sortColumnName) {
      this.rows.update((rows) =>
        rows.sort((rowA: any, rowB: any) => {
          const valueA = rowA.cols[sortYear] ? rowA.cols[sortYear][sortColumnName] : null;
          const valueB = rowB.cols[sortYear] ? rowB.cols[sortYear][sortColumnName] : null;

          if (!rowA.cols[sortYear] && !rowB.cols[sortYear]) {
            return 0;
          }
          if (!rowA.cols[sortYear]) {
            return 1;
          }
          if (!rowB.cols[sortYear]) {
            return -1;
          }

          if (valueA === null && valueB === null) {
            return 0;
          }
          if (valueA === null) {
            return this.sortDescending() ? 1 : -1;
          }
          if (valueB === null) {
            return this.sortDescending() ? -1 : 1;
          }
          if (valueA < valueB) {
            return this.sortDescending() ? 1 : -1;
          }
          if (valueA > valueB) {
            return this.sortDescending() ? -1 : 1;
          }
          return 0;
        })
      );
    }
  }

  protected getYearData(
    row: BuildingMeasureTableItemDTOWithYearData,
    year?: number | null
  ): { [property: string]: unknown } | undefined {
    return row.cols?.[year || -1] as { [property: string]: unknown } | undefined;
  }

  protected setFocusIndex(index: number | undefined): void {
    if (this.openIndex()) {
      return;
    }

    setTimeout(() => {
      // Timeout is needed to prevent ExpressionChangedAfterItHasBeenCheckedError
      this.focusIndex.set(index);
      if (index === undefined) {
        this.openIndex.set(undefined);
        this.retainFocusIndex.set(false);
      }
    });
  }

  protected clearFocusIndex(event: FocusEvent | MouseEvent): void {
    setTimeout(() => {
      // Timeout is needed for timing issue with opening dropdown
      if (
        this.retainFocusIndex() ||
        (event.relatedTarget as HTMLElement)?.closest('.roadmap-table-measure-header-item')
      ) {
        return;
      }

      this.setFocusIndex(undefined);
    });
  }

  protected saveMeasureStatus(result: { measure: BuildingMeasureTableItemDTO; newStatus: MeasureStatusDTO }): void {
    this.unsubscribeMeasureImplementationYearModalOpenChangeSubscription();
    this.openIndex.set(undefined);

    this.isMeasureImplementationYearModalOpen = false;
    this.measureImplementationYearModalOpenChangeSubscription =
      this.roadmapService.measureImplementationYearModalOpenChange.subscribe((isOpen) => {
        this.isMeasureImplementationYearModalOpen = isOpen;
        if (!isOpen) {
          this.retainFocusIndex.set(false);
          this.unsubscribeMeasureImplementationYearModalOpenChangeSubscription();
        }
      });

    setTimeout(() => {
      if (this.restoreFocusElement) {
        // Timeout is needed to make sure the dropdown is closed and aria-hidden is removed
        this.restoreFocusElement.focus();
        this.restoreFocusElement = undefined;
      }

      if (!this.isMeasureImplementationYearModalOpen) {
        this.retainFocusIndex.set(false);
      }
    });

    this.measureStatusUpdate.emit({ buildingMeasure: result.measure, newStatus: result.newStatus });
  }

  private unsubscribeMeasureImplementationYearModalOpenChangeSubscription(): void {
    if (this.measureImplementationYearModalOpenChangeSubscription) {
      this.measureImplementationYearModalOpenChangeSubscription.unsubscribe();
      this.measureImplementationYearModalOpenChangeSubscription = undefined;
    }
  }
}
